import type { ReactNode } from "react";
import "./DashboardBanner.scss";

interface DashboardBannerProps {
  menuButton?: ReactNode;
}

export const DashboardBanner = ({ menuButton }: DashboardBannerProps) => {
  return (
    <div className="dashboard-banner">
      {/* Background blobs */}
      <div className="banner-blob banner-blob--1" />
      <div className="banner-blob banner-blob--2" />
      <div className="banner-blob banner-blob--3" />

      {/* Dashed lines decoration */}
      <svg className="banner-lines" viewBox="0 0 800 100" preserveAspectRatio="none">
        <path
          d="M0 60 Q200 20 400 50 T800 30"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="8 6"
          fill="none"
        />
        <path
          d="M0 80 Q300 40 500 70 T800 50"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="4 8"
          fill="none"
        />
      </svg>

      {/* Dot grid pattern */}
      <div className="banner-dots" />

      {/* Menu button overlay (top-right) */}
      {menuButton && (
        <div className="banner-menu-overlay">
          {menuButton}
        </div>
      )}

      {/* Floating icons - left side */}
      <div className="banner-float banner-float--cloud">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
        </svg>
      </div>
      <div className="banner-float banner-float--lock">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div className="banner-float banner-float--share">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      </div>

      {/* Floating icons - right side */}
      <div className="banner-float banner-float--zap">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      </div>
      <div className="banner-float banner-float--cpu">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
          <rect x="9" y="9" width="6" height="6" />
          <line x1="9" y1="1" x2="9" y2="4" />
          <line x1="15" y1="1" x2="15" y2="4" />
          <line x1="9" y1="20" x2="9" y2="23" />
          <line x1="15" y1="20" x2="15" y2="23" />
          <line x1="20" y1="9" x2="23" y2="9" />
          <line x1="20" y1="14" x2="23" y2="14" />
          <line x1="1" y1="9" x2="4" y2="9" />
          <line x1="1" y1="14" x2="4" y2="14" />
        </svg>
      </div>
      <div className="banner-float banner-float--globe">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      </div>

      {/* Center content — Techify logo + Excalidraw branding */}
      <div className="banner-content">
        {/* Techify logo: blue gradient hexagon with network nodes */}
        <svg className="banner-techify-logo" width="48" height="52" viewBox="0 0 48 52" style={{ transform: "rotate(180deg)" }}>
          <defs>
            <linearGradient id="techify-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1d4ed8" />
            </linearGradient>
          </defs>
          {/* Hexagon shape */}
          <polygon
            points="24,2 44,14 44,38 24,50 4,38 4,14"
            fill="url(#techify-grad)"
            stroke="none"
          />
          {/* Network nodes (Y-pattern) */}
          <circle cx="24" cy="16" r="3" fill="#fff" />
          <circle cx="16" cy="34" r="3" fill="#fff" />
          <circle cx="32" cy="34" r="3" fill="#fff" />
          <circle cx="24" cy="26" r="2.5" fill="#fff" />
          <line x1="24" y1="16" x2="24" y2="26" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="24" y1="26" x2="16" y2="34" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="24" y1="26" x2="32" y2="34" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>

        <span className="banner-byline">Excalidraw by Techify</span>
        <span className="banner-tagline">Crie, colabore e organize seus quadros</span>
      </div>

      {/* Small colored dots decoration */}
      <div className="banner-dot-accent banner-dot-accent--1" />
      <div className="banner-dot-accent banner-dot-accent--2" />
      <div className="banner-dot-accent banner-dot-accent--3" />
      <div className="banner-dot-accent banner-dot-accent--4" />
    </div>
  );
};
